import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, openEventStream } from "./api";
import { applyMessageEvent } from "./messages";
import type {
  AgentInfo,
  AppState,
  CommandInfo,
  OcipEvent,
  PermissionRequest,
  ProjectEntry,
  Provider,
  QuestionRequest,
  Session,
  SessionMessage,
  SessionPrefs,
  SessionStatusInfo,
} from "./types";
import { Login } from "./components/Login";
import { Sidebar } from "./components/Sidebar";
import { ChatView, type Attachment } from "./components/ChatView";
import { RightPanel } from "./components/RightPanel";
import { NewSessionModal, PermissionsModal, ProviderKeyModal, QuestionsModal, RenameModal } from "./components/Modals";
import { SettingsModal } from "./components/SettingsModal";

type ToastKind = "info" | "error" | "success";
interface Toast {
  id: number;
  msg: string;
  kind: ToastKind;
}

let toastSeq = 1;

export default function App() {
  // ── Autenticação ─────────────────────────────────────────────────
  const [user, setUser] = useState<string | null>(null);
  const [booting, setBooting] = useState(true);

  // ── Dados globais ────────────────────────────────────────────────
  const [appState, setAppState] = useState<AppState>({ favorites: [], theme: "system", lastProject: null });
  const [projects, setProjects] = useState<ProjectEntry[]>([]);
  const [directory, setDirectory] = useState<string>("");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionStatus, setSessionStatus] = useState<Record<string, SessionStatusInfo>>({});
  const [providers, setProviders] = useState<Provider[]>([]);
  const [connectedProviders, setConnectedProviders] = useState<string[]>([]);
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [commands, setCommands] = useState<CommandInfo[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // ── Sessão ativa ─────────────────────────────────────────────────
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<SessionMessage[]>([]);
  const [model, setModel] = useState<{ providerID: string; modelID: string } | null>(null);
  const [agent, setAgent] = useState("");
  const prefsKey = `${directory}\0${activeId ?? ""}`;
  const [loadedPrefs, setLoadedPrefs] = useState<{ key: string; prefs: SessionPrefs } | null>(null);
  const [prefsLoadFailed, setPrefsLoadFailed] = useState<string | null>(null);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const prefsKeyRef = useRef(prefsKey);
  prefsKeyRef.current = prefsKey;
  const prefsSavingRef = useRef(false);
  const prefsRequestRef = useRef(0);
  const activePrefs = loadedPrefs?.key === prefsKey ? loadedPrefs.prefs : null;
  const allowAppRun = activePrefs?.allowAppRun === true;
  const allowAllPermissions = activePrefs?.allowAllPermissions === true;
  const sessionPrefsLoading = !!activeId && (!activePrefs || prefsLoadFailed === prefsKey);

  // ── Interações ───────────────────────────────────────────────────
  const [permissions, setPermissions] = useState<PermissionRequest[]>([]);
  const [questions, setQuestions] = useState<QuestionRequest[]>([]);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [modal, setModal] = useState<"none" | "new" | "rename" | "settings">("none");
  // Modal de credencial de provedor + ação pendente a continuar após salvar.
  const [keyModal, setKeyModal] = useState<{
    provider: Provider;
    pendingModel?: { providerID: string; modelID: string } | null;
    pendingCreate?: {
      title?: string;
      agent?: string;
      model?: { id: string; providerID: string };
      allowAppRun?: boolean;
      allowAllPermissions?: boolean;
    } | null;
  } | null>(null);
  const [keySaving, setKeySaving] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(420);
  const [panelCollapsed, setPanelCollapsed] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const directoryRef = useRef(directory);
  directoryRef.current = directory;
  const promptsRequestRef = useRef(0);

  const pushToast = useCallback((msg: string, kind: ToastKind = "info") => {
    const id = toastSeq++;
    setToasts((t) => [...t, { id, msg, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000);
  }, []);

  /** Minimiza/restaura o painel direito via duplo clique na linha divisória. */
  const togglePanelCollapse = useCallback(() => {
    setPanelCollapsed((c) => !c);
  }, []);

  // ── Boot ─────────────────────────────────────────────────────────
  // Carrega estado e projetos. Roda ao montar E após o login: o boot inicial
  // pode abortar com 401 (sessão expirada) e, nesse caso, precisa re-executar
  // quando o usuário autenticar pela tela de login.
  const loadBootstrap = useCallback(async () => {
    try {
      const [state, proj] = await Promise.all([api.getState(), api.projects()]);
      setAppState(state);
      setProjects(proj.allowed);
      const initial = state.lastProject && proj.allowed.some((p) => p.path === state.lastProject)
        ? state.lastProject
        : proj.allowed[0]?.path ?? "";
      setDirectory(initial);
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        pushToast((err as Error).message, "error");
      }
    }
  }, [pushToast]);

  useEffect(() => {
    void (async () => {
      try {
        const me = await api.me();
        setUser(me.user);
        await loadBootstrap();
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) {
          pushToast((err as Error).message, "error");
        }
      } finally {
        setBooting(false);
      }
    })();
  }, [pushToast, loadBootstrap]);

  // ── Carregamentos por diretório ──────────────────────────────────
  const refreshSessions = useCallback(async (dir: string, search?: string) => {
    try {
      const [list, status] = await Promise.all([
        api.sessions(dir, search || undefined),
        api.sessionStatus(dir),
      ]);
      setSessions(list);
      setSessionStatus(status);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }, [pushToast]);

  const refreshPrompts = useCallback(async (dir: string) => {
    const requestSeq = ++promptsRequestRef.current;
    try {
      const [perms, qs] = await Promise.all([api.permissions(dir), api.questions(dir)]);
      if (directoryRef.current !== dir || requestSeq !== promptsRequestRef.current) return;
      setPermissions(perms);
      setQuestions(qs);
    } catch {
      /* servidor pode estar reiniciando */
    }
  }, []);

  const refreshAux = useCallback(async (dir: string) => {
    try {
      const [prov, ags, cmds] = await Promise.all([api.providers(dir), api.agents(dir), api.commands(dir)]);
      setProviders(prov.all ?? []);
      setConnectedProviders(prov.connected ?? []);
      setAgents(ags);
      setCommands(cmds);
    } catch (err) {
      pushToast(`Falha ao carregar provedores/agentes: ${(err as Error).message}`, "error");
    }
  }, [pushToast]);

  useEffect(() => {
    if (!user || !directory) return;
    void refreshSessions(directory, searchTerm);
    void refreshPrompts(directory);
    void refreshAux(directory);
  }, [user, directory, refreshSessions, refreshPrompts, refreshAux]);

  // Catálogo de provedores segue o motor: quando o OpenCode é atualizado e
  // reinicia (atualização automática), recarregamos provedores/agentes/comandos
  // sem exigir ação do usuário.
  const lastEngineVersion = useRef<string | null>(null);
  useEffect(() => {
    if (!user || !directory) return;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const s = await api.updateStatus();
          if (s.engineVersion && lastEngineVersion.current && s.engineVersion !== lastEngineVersion.current) {
            pushToast(
              `OpenCode atualizado para ${s.engineVersion} — catálogo de provedores recarregado.`,
              "success",
            );
            void refreshAux(directory);
          }
          if (s.engineVersion) lastEngineVersion.current = s.engineVersion;
        } catch {
          /* indisponível neste ciclo */
        }
      })();
    }, 60_000);
    return () => clearInterval(timer);
  }, [user, directory, refreshAux, pushToast]);

  // ── Mensagens da sessão ativa ────────────────────────────────────
  const loadMessages = useCallback(
    async (dir: string, id: string) => {
      try {
        const msgs = await api.messages(dir, id);
        setMessages(msgs);
      } catch (err) {
        pushToast((err as Error).message, "error");
      }
    },
    [pushToast],
  );

  useEffect(() => {
    if (!directory || !activeId) {
      setMessages([]);
      return;
    }
    void loadMessages(directory, activeId);
  }, [directory, activeId, loadMessages]);

  // Atualização periódica de estado (fallback dos eventos SSE). Túneis/CDN que
  // acumulam o stream SSE (Quick Tunnel do Cloudflare, medido) entregam atrasado
  // ou nunca; o polling de mensagens é o que faz a resposta aparecer em tempo
  // real sem recarregar. A RECONCILIAÇÃO DE MENSAGENS NÃO DEPENDE DE turnActive:
  // uma tarefa real pode levar muito mais de 90s e terminar depois da janela de
  // "turno"; se o polling de mensagens parasse aí, a resposta só apareceria no
  // reload — exatamente o bug relatado. Por isso loadMessages roda sempre que há
  // sessão ativa; turnActive apenas acelera o ritmo durante a geração.
  useEffect(() => {
    if (!user || !directory) return;
    let lastMsgs = 0;
    let lastAux = 0;
    const timer = setInterval(() => {
      const sid = activeIdRef.current;
      if (!sid) return;
      const st = sessionStatusRef.current[sid];
      // Turno em andamento: status busy/retry, envio recente OU a última mensagem
      // ainda é do assistente sem `time.completed` (motor ainda gerando).
      const arr = messagesRef.current;
      const lastMsg = arr[arr.length - 1];
      const generating =
        lastMsg?.info?.role === "assistant" && !lastMsg?.info?.time?.completed;
      const turnActive =
        st?.type === "busy" ||
        st?.type === "retry" ||
        generating ||
        Date.now() - lastSendAtRef.current < 90000;
      // Ritmo: mensagens rápidas durante a geração, cadenciadas quando ocioso.
      const msgInterval = turnActive ? 2500 : 8000;
      const now = Date.now();
      if (now - lastMsgs >= msgInterval) {
        lastMsgs = now;
        void loadMessages(directory, sid);
      }
      if (now - lastAux >= 20000) {
        lastAux = now;
        void refreshSessions(directory, searchTermRef.current);
        void refreshPrompts(directory);
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [user, directory, refreshSessions, refreshPrompts, loadMessages]);

  // Permissões novas detectadas por polling também abrem o modal (em túnel,
  // o evento permission.asked não chega pelo SSE).
  useEffect(() => {
    for (const q of permissions) {
      if (!announcedPerms.current.has(q.id)) {
        announcedPerms.current.add(q.id);
        setShowPermissions(true);
      }
    }
  }, [permissions]);

  // ── Preferências por sessão ──────────────────────────────────────
  useEffect(() => {
    if (!directory || !activeId) {
      setLoadedPrefs(null);
      return;
    }
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let reportedError = false;
    const requestSeq = ++prefsRequestRef.current;
    setPrefsLoadFailed(null);
    const loadPrefs = async () => {
      try {
        const prefs = await api.sessionPrefs(directory, activeId);
        if (cancelled || requestSeq !== prefsRequestRef.current) return;
        setLoadedPrefs({ key: prefsKey, prefs });
        setPrefsLoadFailed(null);
        setModel(prefs.model ?? null);
      } catch (err) {
        if (cancelled || requestSeq !== prefsRequestRef.current) return;
        setPrefsLoadFailed(prefsKey);
        if (!reportedError) {
          reportedError = true;
          pushToast(`Não foi possível carregar as permissões da sessão: ${(err as Error).message}. Tentando novamente.`, "error");
        }
        retryTimer = setTimeout(() => void loadPrefs(), 5000);
      }
    };
    void loadPrefs();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [directory, activeId, prefsKey, pushToast]);

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeId) ?? null,
    [sessions, activeId],
  );

  // ── Stream de eventos (SSE) ──────────────────────────────────────
  useEffect(() => {
    if (!user || !directory) return;

    const close = openEventStream(directory, (ev: OcipEvent) => {
      const p = ev.properties as Record<string, unknown>;
      switch (ev.type) {
        case "message.updated":
        case "message.removed":
        case "message.part.updated":
        case "message.part.delta":
        case "message.part.removed": {
          const sessionID = String(p.sessionID ?? "");
          if (sessionID && sessionID === activeIdRef.current) {
            setMessages((prev) => applyMessageEvent(prev, ev));
          }
          break;
        }
        case "session.created":
        case "session.deleted":
          void refreshSessions(directoryRef.current, searchTermRef.current);
          break;
        case "session.updated": {
          const info = p.info as Session | undefined;
          if (info) {
            setSessions((prev) => {
              const idx = prev.findIndex((s) => s.id === info.id);
              if (idx === -1) return [info, ...prev];
              const copy = [...prev];
              copy[idx] = { ...copy[idx], ...info };
              return copy;
            });
          }
          break;
        }
        case "session.status": {
          const sid = String(p.sessionID ?? "");
          const status = (p.status ?? p) as SessionStatusInfo;
          if (sid) setSessionStatus((prev) => ({ ...prev, [sid]: status }));
          // O motor 1.18.33 sinaliza o fim do turno via session.status
          // {type:"idle"} (session.idle não é emitido) — reconcilia com o servidor.
          if (status.type === "idle" && sid === activeIdRef.current && directoryRef.current) {
            void loadMessages(directoryRef.current, sid);
            void refreshSessions(directoryRef.current, searchTermRef.current);
          }
          break;
        }
        case "session.idle": {
          const sid = String(p.sessionID ?? "");
          if (sid) setSessionStatus((prev) => ({ ...prev, [sid]: { type: "idle" } }));
          if (sid === activeIdRef.current && directoryRef.current) {
            void loadMessages(directoryRef.current, sid);
            void refreshSessions(directoryRef.current, searchTermRef.current);
          }
          break;
        }
        case "session.error": {
          const msg = (p.error as { message?: string })?.message ?? "Erro na execução da sessão";
          pushToast(msg, "error");
          break;
        }
        case "permission.asked": {
          // O servidor resolve pedidos de sessões com aprovação automática.
          // Só a lista reconciliada abre o modal para pedidos ainda pendentes.
          void refreshPrompts(directoryRef.current);
          break;
        }
        case "permission.replied":
        case "question.replied":
        case "question.rejected":
        case "question.asked": {
          if (ev.type === "question.asked") setShowQuestions(true);
          void refreshPrompts(directoryRef.current);
          break;
        }
        default:
          break;
      }
    });
    return close;
  }, [user, directory, refreshSessions, refreshPrompts, loadMessages, pushToast]);

  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  const sessionStatusRef = useRef(sessionStatus);
  sessionStatusRef.current = sessionStatus;
  /** Espelho de `messages` para o polling decidir se ainda está gerando. */
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  /** Timestamp do último envio — ativa o polling rápido de mensagens. */
  const lastSendAtRef = useRef(0);
  /** Permissões já anunciadas (SSE ou polling) para abrir o modal uma única vez. */
  const announcedPerms = useRef<Set<string>>(new Set());

  // ── Ações ────────────────────────────────────────────────────────
  /** Provedor exige chave de API (declara variável de credencial) e ainda não foi conectado. */
  function providerNeedsKey(providerID: string): boolean {
    const p = providers.find((x) => x.id === providerID);
    return !!p && (p.env?.length ?? 0) > 0 && !connectedProviders.includes(providerID);
  }

  async function performCreateSession(data: {
    title?: string;
    agent?: string;
    model?: { id: string; providerID: string };
    allowAppRun?: boolean;
    allowAllPermissions?: boolean;
  }) {
    if (!directory) {
      pushToast("Selecione um projeto antes de criar a sessão.", "error");
      return;
    }
    try {
      const s = await api.createSession(directory, data);
      // Persiste as preferências escolhidas para esta sessão.
      await api.putSessionPrefs(directory, s.id, {
        model: data.model ? { providerID: data.model.providerID, modelID: data.model.id } : undefined,
        allowAppRun: data.allowAppRun === true,
        allowAllPermissions: data.allowAllPermissions === true,
      });
      setModal("none");
      setActiveId(s.id);
      await refreshSessions(directory, searchTerm);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  /** Salva a chave do provedor no servidor e continua a ação pendente. */
  async function handleSaveProviderKey(key: string) {
    if (!keyModal) return;
    setKeySaving(true);
    setKeyError(null);
    try {
      const res = await api.setProviderAuth(keyModal.provider.id, key);
      setConnectedProviders(res.connected ?? []);
      pushToast(`Credencial de ${keyModal.provider.name} salva no servidor.`, "success");
      const pendingModel = keyModal.pendingModel;
      const pendingCreate = keyModal.pendingCreate;
      setKeyModal(null);
      if (pendingModel) {
        setModel(pendingModel);
        if (directory && activeId) {
          void api.putSessionPrefs(directory, activeId, { model: pendingModel }).catch(() => undefined);
        }
      } else if (pendingCreate) {
        void performCreateSession(pendingCreate);
      }
    } catch (err) {
      setKeyError((err as Error).message);
    } finally {
      setKeySaving(false);
    }
  }

  function handleModelChange(m: { providerID: string; modelID: string } | null) {
    // Provedor sem credencial: pede a chave antes de fixar o modelo.
    if (m && providerNeedsKey(m.providerID)) {
      const p = providers.find((x) => x.id === m.providerID);
      if (p) {
        setKeyError(null);
        setKeyModal({ provider: p, pendingModel: m });
        return;
      }
    }
    setModel(m);
    // Modelo é salvo por sessão e restaurado ao reabri-la.
    if (m && directory && activeId) {
      void api.putSessionPrefs(directory, activeId, { model: m }).catch(() => undefined);
    }
  }

  async function handleToggleAllowAppRun() {
    if (!directory || !activeId) {
      pushToast("Selecione uma sessão para alterar esta opção.", "error");
      return;
    }
    if (sessionPrefsLoading || prefsSavingRef.current || allowAllPermissions) return;
    const dir = directory;
    const id = activeId;
    const key = prefsKey;
    const next = !allowAppRun;
    prefsSavingRef.current = true;
    setPermissionsSaving(true);
    try {
      const prefs = await api.putSessionPrefs(dir, id, { allowAppRun: next });
      if (prefsKeyRef.current === key) {
        prefsRequestRef.current += 1;
        setLoadedPrefs({ key, prefs });
        setPrefsLoadFailed(null);
      }
      pushToast(
        next
          ? "Rodar aplicação ativada nesta sessão."
          : "Rodar aplicação desativada: comandos comuns continuam permitidos, mas a aplicação não sobe nesta sessão.",
        "success",
      );
    } catch (err) {
      pushToast((err as Error).message, "error");
    } finally {
      prefsSavingRef.current = false;
      setPermissionsSaving(false);
    }
  }

  async function handleAllowAllPermissionsChange(allow: boolean, sessionID = activeId) {
    if (!directory || !sessionID || prefsSavingRef.current) return;
    const dir = directory;
    const key = `${dir}\0${sessionID}`;
    prefsSavingRef.current = true;
    setPermissionsSaving(true);
    try {
      const prefs = await api.putSessionPrefs(dir, sessionID, { allowAllPermissions: allow });
      if (prefsKeyRef.current === key) {
        prefsRequestRef.current += 1;
        setLoadedPrefs({ key, prefs });
        setPrefsLoadFailed(null);
      }
      // A nova preferência já foi persistida e as pendências foram processadas.
      await refreshPrompts(dir);
      pushToast(
        allow ? "Permitir tudo ativado para esta sessão." : "Permitir tudo desativado para esta sessão.",
        "success",
      );
    } catch (err) {
      pushToast((err as Error).message, "error");
    } finally {
      prefsSavingRef.current = false;
      setPermissionsSaving(false);
    }
  }

  async function handleSend(text: string, files: Attachment[]) {
    if (!directory || !activeId) return;
    lastSendAtRef.current = Date.now();
    // Otimista: marca o turno como ativo para o polling rápido assumir
    // imediatamente (o SSE pode estar bloqueado em túneis que acumulam stream).
    setSessionStatus((prev) => ({ ...prev, [activeId]: { type: "busy" } }));
    try {
      await api.prompt(directory, activeId, {
        text,
        files: files.map((f) => ({ uri: f.uri, name: f.name })),
        model: model ?? undefined,
        agent: agent || undefined,
      });
      await loadMessages(directory, activeId);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleUpload(file: File): Promise<Attachment> {
    const res = await api.upload(file);
    return { uri: res.uri, name: res.name, size: res.size };
  }

  async function handleAbort() {
    if (!directory || !activeId) return;
    try {
      await api.abort(directory, activeId);
      pushToast("Execução interrompida.", "success");
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleCreateSession(data: {
    title?: string;
    agent?: string;
    model?: { id: string; providerID: string };
    allowAppRun?: boolean;
    allowAllPermissions?: boolean;
  }) {
    // Modelo de provedor sem credencial: pede a chave e cria a sessão em seguida.
    if (data.model && providerNeedsKey(data.model.providerID)) {
      const p = providers.find((x) => x.id === data.model!.providerID);
      if (p) {
        setKeyError(null);
        setKeyModal({ provider: p, pendingCreate: data });
        return;
      }
    }
    await performCreateSession(data);
  }

  async function handleRename(id: string, title: string) {
    if (!directory || !title) return;
    try {
      await api.updateSession(directory, id, { title });
      setModal("none");
      await refreshSessions(directory, searchTerm);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleDelete(id: string) {
    if (!directory) {
      pushToast("Selecione um projeto para esta ação.", "error");
      return;
    }
    // Confirmação com o impacto real: a sessão e seus arquivos.
    let fileCount = 0;
    try {
      const tracked = await api.sessionFiles(directory, id);
      fileCount = tracked.files.length;
    } catch {
      fileCount = 0;
    }
    const msg =
      fileCount > 0
        ? `Excluir esta sessão, o histórico e os ${fileCount} arquivo(s) criados/alterados por ela? Esta ação não pode ser desfeita.`
        : "Excluir esta sessão e todo o seu histórico?";
    if (!window.confirm(msg)) return;
    try {
      const result = await api.deleteSession(directory, id);
      if (activeId === id) setActiveId(null);
      await refreshSessions(directory, searchTerm);
      pushToast(
        result.filesDeleted.length > 0
          ? `Sessão excluída com ${result.filesDeleted.length} arquivo(s).`
          : "Sessão excluída.",
        "success",
      );
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleFork(messageID?: string) {
    if (!directory || !activeId) return;
    try {
      const s = await api.fork(directory, activeId, messageID);
      setActiveId(s.id);
      await refreshSessions(directory, searchTerm);
      pushToast("Sessão bifurcada.", "success");
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleSummarize() {
    if (!directory || !activeId) return;
    if (!model) {
      pushToast("Selecione um modelo no compositor para resumir a sessão.", "error");
      return;
    }
    try {
      await api.summarize(directory, activeId, model);
      pushToast("Resumo solicitado.", "success");
      await refreshSessions(directory, searchTerm);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleShareToggle() {
    if (!directory || !activeSession) return;
    try {
      if (activeSession.share?.url) {
        await api.unshare(directory, activeSession.id);
        pushToast("Compartilhamento desativado.", "success");
      } else {
        await api.share(directory, activeSession.id);
        pushToast("Sessão compartilhada.", "success");
      }
      await refreshSessions(directory, searchTerm);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleRevert(messageID: string) {
    if (!directory || !activeId) return;
    try {
      await api.revert(directory, activeId, messageID);
      await loadMessages(directory, activeId);
      pushToast("Mensagem revertida.", "success");
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleUnrevert() {
    if (!directory || !activeId) return;
    try {
      await api.unrevert(directory, activeId);
      await loadMessages(directory, activeId);
      pushToast("Mensagens restauradas.", "success");
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleInit() {
    if (!directory || !activeId) return;
    if (!model) {
      pushToast("Selecione um modelo para gerar o AGENTS.md.", "error");
      return;
    }
    try {
      await api.initSession(directory, activeId, {
        modelID: model.modelID,
        providerID: model.providerID,
        messageID: `msg${Date.now().toString(36)}`,
      });
      pushToast("Inicialização do projeto solicitada (AGENTS.md).", "success");
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleRunCommand(command: string, args: string) {
    if (!directory || !activeId) return;
    try {
      await api.command(directory, activeId, { command, arguments: args, agent: agent || undefined });
      await loadMessages(directory, activeId);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handlePermissionReply(id: string, reply: "once" | "always" | "reject", message?: string) {
    if (!directory) {
      pushToast("Selecione um projeto para esta ação.", "error");
      return;
    }
    try {
      await api.replyPermission(directory, id, reply, message);
      const rest = permissions.filter((p) => p.id !== id);
      setPermissions(rest);
      if (rest.length === 0) setShowPermissions(false);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleQuestionReply(id: string, answers: string[][]) {
    if (!directory) {
      pushToast("Selecione um projeto para esta ação.", "error");
      return;
    }
    try {
      await api.replyQuestion(directory, id, answers);
      const rest = questions.filter((q) => q.id !== id);
      setQuestions(rest);
      if (rest.length === 0) setShowQuestions(false);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  async function handleQuestionReject(id: string) {
    if (!directory) {
      pushToast("Selecione um projeto para esta ação.", "error");
      return;
    }
    try {
      await api.rejectQuestion(directory, id);
      const rest = questions.filter((q) => q.id !== id);
      setQuestions(rest);
      if (rest.length === 0) setShowQuestions(false);
    } catch (err) {
      pushToast((err as Error).message, "error");
    }
  }

  function toggleFavorite(id: string) {
    const has = appState.favorites.includes(id);
    const favorites = has ? appState.favorites.filter((f) => f !== id) : [...appState.favorites, id];
    setAppState((s) => ({ ...s, favorites }));
    void api.putState({ favorites }).catch(() => pushToast("Não foi possível salvar o favorito.", "error"));
  }

  function toggleTheme() {
    const order: Array<AppState["theme"]> = ["light", "dark", "system"];
    const next = order[(order.indexOf(appState.theme) + 1) % order.length];
    const effective = next === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
      : next;
    document.documentElement.dataset.theme = effective;
    localStorage.setItem("oc-theme", next);
    setAppState((s) => ({ ...s, theme: next }));
    void api.putState({ theme: next }).catch(() => undefined);
    pushToast(`Tema: ${next === "light" ? "claro" : next === "dark" ? "escuro" : "sistema"}`);
  }

  function changeDirectory(dir: string) {
    setDirectory(dir);
    setActiveId(null);
    void api.putState({ lastProject: dir }).catch(() => undefined);
  }

  // ── Atalhos de teclado ───────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSidebarOpen(true);
        const input = document.querySelector<HTMLInputElement>('.search-box input');
        input?.focus();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
        e.preventDefault();
        setModal("new");
      }
      if ((e.ctrlKey || e.metaKey) && e.key === ",") {
        e.preventDefault();
        setModal("settings");
      }
      if (e.key === "Escape") {
        setSidebarOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const handler = () => setSidebarOpen(true);
    document.addEventListener("oc:open-sidebar", handler);
    return () => document.removeEventListener("oc:open-sidebar", handler);
  }, []);

  // ── Render ───────────────────────────────────────────────────────
  if (booting) {
    return (
      <div className="login-wrap">
        <p style={{ color: "var(--text-muted)" }}>Carregando Opencode Cloud…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <Login
        onLogin={(u) => {
          setUser(u);
          void loadBootstrap();
        }}
      />
    );
  }

  const busy = !!activeSession && sessionStatus[activeSession.id]?.type !== undefined &&
    sessionStatus[activeSession.id]?.type !== "idle";

  return (
    <>
      <div className="app-shell">
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          user={user}
          theme={appState.theme}
          onToggleTheme={toggleTheme}
          onLogout={() => {
            void api.logout().then(() => setUser(null));
          }}
          onOpenSettings={() => setModal("settings")}
          projects={projects}
          directory={directory}
          onDirectoryChange={changeDirectory}
          sessions={sessions}
          sessionStatus={sessionStatus}
          activeSessionId={activeId}
          onSelectSession={(id) => {
            setActiveId(id);
            setSidebarOpen(false);
          }}
          onNewSession={() => setModal("new")}
          onRenameSession={(id) => {
            setActiveId(id);
            setModal("rename");
          }}
          onDeleteSession={handleDelete}
          search={searchTerm}
          onSearch={(term) => {
            setSearchTerm(term);
            if (directory) void refreshSessions(directory, term);
          }}
          appState={appState}
          onToggleFavorite={toggleFavorite}
        />

        <ChatView
          session={activeSession}
          messages={messages}
          busy={busy}
          status={activeSession ? sessionStatus[activeSession.id] : undefined}
          providers={providers}
          connected={connectedProviders}
          agents={agents}
          commands={commands}
          model={model}
          onModelChange={handleModelChange}
          agent={agent}
          onAgentChange={setAgent}
          allowAppRun={allowAppRun}
          allowAllPermissions={allowAllPermissions}
          permissionsSaving={permissionsSaving}
          sessionPrefsLoading={sessionPrefsLoading}
          onAllowAllPermissionsChange={(allow) => void handleAllowAllPermissionsChange(allow)}
          onToggleAllowAppRun={() => void handleToggleAllowAppRun()}
          onSend={handleSend}
          onAbort={handleAbort}
          onUpload={handleUpload}
          onFork={(mid) => void handleFork(mid)}
          onSummarize={() => void handleSummarize()}
          onShareToggle={() => void handleShareToggle()}
          onRevert={(mid) => void handleRevert(mid)}
          onUnrevert={() => void handleUnrevert()}
          onInit={() => void handleInit()}
          onRunCommand={(cmd, args) => void handleRunCommand(cmd, args)}
          onOpenSettings={() => setModal("settings")}
        />

        <RightPanel
          directory={directory}
          session={activeSession}
          sessionStatus={activeSession ? sessionStatus[activeSession.id] : undefined}
          onOpenSession={(id) => setActiveId(id)}
          onWidthChange={setPanelWidth}
          collapsed={panelCollapsed}
          onToggleCollapse={togglePanelCollapse}
        />
      </div>

      {/* Largura personalizada do painel; minimizado vira só a linha divisória */}
      <style>{`.right-panel{width:${panelCollapsed ? "12px" : `${panelWidth}px`};${panelCollapsed ? "min-width:12px;max-width:12px;" : ""}}`}</style>

      {sidebarOpen ? <div className="backdrop-mobile" onClick={() => setSidebarOpen(false)} /> : null}

      {modal === "new" ? (
        <NewSessionModal
          providers={providers}
          connected={connectedProviders}
          agents={agents}
          onCreate={(data) => void handleCreateSession(data)}
          onClose={() => setModal("none")}
        />
      ) : null}

      {modal === "rename" && activeSession ? (
        <RenameModal
          current={activeSession.title ?? ""}
          onRename={(title) => void handleRename(activeSession.id, title)}
          onClose={() => setModal("none")}
        />
      ) : null}

      {modal === "settings" ? (
        <SettingsModal
          directory={directory}
          sessionId={activeId}
          sessionTitle={activeSession?.title ?? null}
          allowAppRun={allowAppRun}
          allowAllPermissions={allowAllPermissions}
          permissionsSaving={permissionsSaving}
          sessionPrefsLoading={sessionPrefsLoading}
          onAllowAllPermissionsChange={(allow) => void handleAllowAllPermissionsChange(allow)}
          onToggleAllowAppRun={() => void handleToggleAllowAppRun()}
          onClose={() => setModal("none")}
          onToast={pushToast}
          onEngineUpdated={() => {
            if (directory) void refreshAux(directory);
          }}
        />
      ) : null}

      {showPermissions && permissions.length > 0 ? (
        <PermissionsModal
          requests={permissions}
          onReply={(id, reply, message) => void handlePermissionReply(id, reply, message)}
          onAllowAllSession={(id) => void handleAllowAllPermissionsChange(true, id)}
          saving={permissionsSaving}
          onClose={() => setShowPermissions(false)}
        />
      ) : null}

      {permissions.length > 0 && !showPermissions ? (
        <button
          className="btn primary"
          style={{ position: "fixed", right: 18, top: 18, zIndex: 55 }}
          onClick={() => setShowPermissions(true)}
        >
          🔒 {permissions.length} permissão(ões) pendente(s)
        </button>
      ) : null}

      {showQuestions && questions.length > 0 ? (
        <QuestionsModal
          requests={questions}
          onReply={(id, answers) => void handleQuestionReply(id, answers)}
          onReject={(id) => void handleQuestionReject(id)}
          onClose={() => setShowQuestions(false)}
        />
      ) : null}

      {questions.length > 0 && !showQuestions ? (
        <button
          className="btn primary"
          style={{ position: "fixed", right: 18, top: 66, zIndex: 55 }}
          onClick={() => setShowQuestions(true)}
        >
          ❓ {questions.length} pergunta(s) do OpenCode
        </button>
      ) : null}

      {keyModal ? (
        <ProviderKeyModal
          provider={keyModal.provider}
          saving={keySaving}
          error={keyError}
          onSave={(k) => void handleSaveProviderKey(k)}
          onClose={() => setKeyModal(null)}
        />
      ) : null}

      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </>
  );
}

// O redutor de eventos de mensagens vive em web/src/messages.ts (puro e
// testado unitariamente em tests/messages.test.ts — imutabilidade garantida).
